import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import _ from 'lodash';
import { routes } from 'env/routes';
import { NoAccessNotice } from 'ui/components/NoAccessNotice';
import withCustomComponent from 'ui/components/withCustomComponent';
import CardDashboard from 'ui/components/cards/CardDashboard';
import BenefitsRow from 'ui/_pages/home/components/BenefitsWidget/BenefitsRowBW';
import LoadingWidget from 'ui/_pages/home/components/LoadingWidget/LoadingWidget';
import { useTranslation } from 'react-i18next';
import { useTeamDiscount } from '@bw-ui/hooks/useTeamDiscount';

const BenefitsWidget = withCustomComponent('BenefitsWidget')(
  inject(
    'appStore',
    'invoicingStore',
    'authStore'
  )(
    observer(({ invoicingStore, componentName }) => {
      const [error, setError] = useState(null);
      const { t } = useTranslation();
      const { teamDiscounts, isLoading: isLoadingTeamDiscount } = useTeamDiscount();
      const { benefits, hasLoadedBenefits, hasBenefits } = invoicingStore;

      useEffect(() => {
        if (!invoicingStore.hasLoadedBenefits) {
          invoicingStore.loadBenefits().catch((ex) => {
            this.setState({ error: t('Could not load your benefits.') });
          });
        }
      }, []);

      const hasError = error != null;
      if (!hasLoadedBenefits || isLoadingTeamDiscount)
        return <LoadingWidget className="" title={t('Benefits')} />;

      return (
        <CardDashboard
          headerMainTitle={t('Benefits')}
          headerActionTitle={t('View all')}
          headerActionUrl={routes.plan}
          // Bruntwood - hidden until coworking plans are available
          // footerActionTitle={t('Join a Plan')}
          // footerActionUrl={routes.plan}
          componentName={componentName}
        >
          <NoAccessNotice error={error} />
          {hasBenefits || teamDiscounts.bookings > 0 ? (
            <div className="mb-24">
              {teamDiscounts.bookings > 0 && (
                <BenefitsRow
                  name={'Bruntwood Discount'}
                  valueNow={teamDiscounts.bookings}
                  unit={t('%')}
                  fullbar
                />
              )}
              {_.take(benefits.TimePasses, 1).map((b, i) => {
                const timeMax = b.TotalMinutes > 0 ? b.TotalMinutes : b.Count;
                const timeNow =
                  b.TotalMinutes > 0 ? b.MinutesLeft : b.PassesLeft;
                const timeUnit = b.TotalMinutes > 0 ? t('mins.') : t('passes');
                return (
                  <BenefitsRow
                    name={b.CreditName}
                    valueMin={0}
                    valueMax={timeMax}
                    valueNow={timeNow}
                    unit={timeUnit}
                    key={i}
                  />
                );
              })}
              {_.take(benefits.BookingCredits, 1).map((b, i) => (
                <BenefitsRow
                  name={b.CreditName}
                  valueMin={0}
                  valueMax={b.TotalCredit}
                  valueNow={b.RemainingCredit}
                  unit={t('Credits')}
                  key={i}
                />
              ))}
              {_.take(benefits.ExtraServices, 1).map((b, i) => {
                const total = b.TotalMinutes
                  ? b.TotalMinutes / 60.0
                  : b.TotalDays || b.TotalMonths || b.TotalWeeks || b.TotalUses;
                const remaining = b.MinutesLeft
                  ? b.MinutesLeft / 60.0
                  : b.DaysLeft || b.MonthsLeft || b.WeeksLeft || b.UsesLeft;

                let unit = '';
                if (b.TotalMinutes > 0)
                  unit = t('hr.', { count: b.TotalMinutes / 60.0 });
                if (b.TotalDays > 0) unit = t('day', { count: b.TotalDays });
                if (b.TotalMonths > 0)
                  unit = t('month', { count: b.TotalMonths });
                if (b.TotalWeeks > 0) unit = t('week', { count: b.TotalWeeks });
                if (b.TotalUses > 0) unit = t('use', { count: b.TotalUses });
                return (
                  <BenefitsRow
                    name={b.CreditName}
                    valueMin={0}
                    valueMax={total}
                    valueNow={remaining}
                    unit={unit}
                    key={i}
                  />
                );
              })}
            </div>
          ) : (
            <>
              <p className="text-gray-700 mt-16">
                {t('You have no benefits currently.')}
              </p>
            </>
          )}
        </CardDashboard>
      );
    })
  )
);

export default BenefitsWidget;
